import type { ApiResponse, InvitationFilters, ListInvitationResponse } from '@fluenceur/types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

const fetchAffiliateInvitations = async (
  filters?: InvitationFilters
): Promise<ListInvitationResponse> => {
  const params = new URLSearchParams();

  if (filters?.programId) {
    params.append('programId', filters.programId);
  }

  if (filters?.search) {
    params.append('search', filters.search);
  }

  if (filters?.status) {
    params.append('status', filters.status);
  }

  if (filters?.sortBy) {
    params.append('sortBy', filters.sortBy);
  }

  if (filters?.sortDirection) {
    params.append('sortDirection', filters.sortDirection);
  }

  if (filters?.page) {
    params.append('page', filters.page.toString());
  }

  if (filters?.perPage) {
    params.append('perPage', filters.perPage.toString());
  }

  const queryString = params.toString();
  const url = `/dashboard/affiliates/invitations${queryString ? `?${queryString}` : ''}`;

  const response = await api.get<ApiResponse<ListInvitationResponse>>(url);

  if (response.data.success === false || !response.data.data) {
    throw new Error(response.data.message ?? 'Failed to fetch program invitations');
  }

  return response.data.data;
};

interface UseProgramInvitationsQueryOptions {
  filters?: InvitationFilters;
  enabled?: boolean;
}

export const useListAffiliateInvitationsQuery = ({
  filters,
  enabled = true,
}: UseProgramInvitationsQueryOptions) => {
  return useQuery<ListInvitationResponse, Error>({
    queryKey: ['program-invitations', filters],
    queryFn: () => fetchAffiliateInvitations(filters),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};
